import React from 'react';
import clsx from 'clsx';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { FunnelIcon } from '@heroicons/react/20/solid';
import { PickerOption, PropertyPicker, chipClass } from './PropertyPicker';

/**
 * Linear's filter bar (#603 Part 2 §2): one chip per active filter, each
 * reading as a sentence — "Status is any of Pending, In Progress" — with
 * an × to remove it, and unset filters offered as quiet "+ Status"
 * buttons. Filters combine with AND ("match all filters").
 *
 * Generic over what is being filtered: violations and work orders each
 * describe their fields and keep their own filter state.
 */
export interface FilterFieldDef {
  field: string;
  options: PickerOption[];
  values: string[];
  onChange: (values: string[]) => void;
  /** Display text for a chosen value; defaults to the option's label. */
  label?: (value: string) => string;
}

interface Props {
  fields: FilterFieldDef[];
  search: string;
  onSearch: (search: string) => void;
  /** Accessible name for the search box, e.g. "Search work orders". */
  searchLabel: string;
  /** Clears every field in one update (not field by field, which would race). */
  onClearAll: () => void;
  shown: number;
  total: number;
  /** Rendered at the far right, after search — the view switch. */
  trailing?: React.ReactNode;
}

const summarise = (values: string[], label: (v: string) => string) =>
  values.length <= 2
    ? values.map(label).join(', ')
    : `${values.length} selected`;

/**
 * One filter, active or not. The picker sits in the same child slot either
 * way (the conditional siblings render as `false`, which keeps positions),
 * so choosing a value — which turns "+ Status" into "Status is Pending" —
 * does not remount the open popover and a multi-select stays open.
 */
const FilterField: React.FC<{ def: FilterFieldDef }> = ({ def }) => {
  const { field, options, values, onChange } = def;
  const label =
    def.label ??
    ((v: string) => options.find((o) => o.value === v)?.label ?? v);
  const active = values.length > 0;
  return (
    <span
      className={clsx(
        'inline-flex items-center text-[13px]',
        active && 'rounded-md border border-line'
      )}
    >
      {active && (
        <span className="px-2 py-1 text-fg-muted">{field}</span>
      )}
      {active && (
        <span className="border-l border-line px-1 text-fg-muted">
          {values.length > 1 ? 'is any of' : 'is'}
        </span>
      )}
      <span className={clsx(active && 'border-l border-line')}>
        <PropertyPicker
          multiple
          options={options}
          value={values}
          onChange={onChange}
          placeholder={`${field}…`}
          ariaLabel={`Filter by ${field.toLowerCase()}`}
          trigger={
            active ? (
              <span className="font-medium">{summarise(values, label)}</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-fg-muted">
                <span aria-hidden>+</span> {field}
              </span>
            )
          }
        />
      </span>
      {active && (
        <button
          type="button"
          onClick={() => onChange([])}
          aria-label={`Remove ${field} filter`}
          className="border-l border-line px-1.5 py-1 text-fg-muted hover:text-fg"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
};

export const FilterBar: React.FC<Props> = ({
  fields,
  search,
  onSearch,
  searchLabel,
  onClearAll,
  shown,
  total,
  trailing,
}) => {
  const anyActive = fields.some((f) => f.values.length > 0);

  return (
    <div className="flex min-h-[3.25rem] flex-wrap items-center gap-2 border-b border-line/60 px-3 py-2">
      <FunnelIcon className="h-4 w-4 text-fg-faint" aria-hidden />

      {fields.map((f) => (
        <FilterField key={f.field} def={f} />
      ))}

      {anyActive ? (
        <button
          type="button"
          className={clsx(chipClass, 'text-fg-muted')}
          onClick={onClearAll}
        >
          Clear
        </button>
      ) : null}

      <div className="ml-auto flex items-center gap-3">
        <span className="text-[12px] text-fg-muted" aria-live="polite">
          {shown === total ? `${total}` : `${shown} of ${total}`}
        </span>
        <label className="flex items-center gap-1.5 rounded-md border border-line px-2 py-1 focus-within:ring-2 focus-within:ring-blue-500">
          <MagnifyingGlassIcon className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search"
            aria-label={searchLabel}
            className="w-40 border-0 bg-transparent p-0 text-[13px] text-fg placeholder:text-zinc-500 focus:outline-none focus:ring-0"
          />
        </label>
        {trailing}
      </div>
    </div>
  );
};

export default FilterBar;
